import React, { useEffect, useRef } from 'react';
import { Download, ArrowUpRight, BadgeCheck } from 'lucide-react';
import { Button } from './ui/button';
import { QRCodeSVG } from 'qrcode.react';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.freelance.skill';
const APP_STORE_URL = 'https://apps.apple.com/in/app/skil-lynk/id6764749128';

const DownloadSection = () => {
  const sectionRef = useRef(null);

  const highlights = [
    'Freelancers and clients in one app',
    'Instant chat and proposal flow',
    'Available on iOS and Android',
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="download"
      className="py-12 md:py-12 relative overflow-hidden"
      ref={sectionRef}
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-12 left-0 h-80 w-80 rounded-full bg-cyan-500/10 blur-[130px] animate-pulse"></div>
        <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-blue-500/10 blur-[150px] animate-pulse delay-1000"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="overflow-hidden rounded-[2rem] border border-slate-200/80 bg-white/90 shadow-[0_30px_80px_rgba(15,23,42,0.12)] backdrop-blur-sm dark:border-white/10 dark:bg-slate-900/80 dark:shadow-[0_30px_80px_rgba(2,8,23,0.6)]">
          <div className="relative">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_20%,rgba(34,211,238,0.12),transparent_28%),radial-gradient(circle_at_82%_78%,rgba(59,130,246,0.12),transparent_30%)]" />

            <div className="grid items-center gap-14 px-6 py-8 md:px-10 md:py-12 lg:grid-cols-[1.1fr_0.9fr] lg:px-14">
              <div className="space-y-8">
                <div className="inline-flex items-center space-x-2 rounded-full border border-slate-200/80 bg-slate-100/90 px-4 py-2 text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200">
                  <Download className="h-5 w-5 text-cyan-500" />
                  <span className="text-sm font-medium">Available Now</span>
                </div>

                <div className="space-y-4">
                  <h2 className="text-4xl font-bold leading-tight text-slate-900 dark:text-white md:text-5xl lg:text-6xl">
                    Get Started with
                    <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-sky-400 to-blue-500">
                      Kaamhub Today
                    </span>
                  </h2>

                  <p className="max-w-2xl text-lg leading-relaxed text-slate-600 dark:text-slate-300 md:text-xl">
                    Download the app and start connecting with talented freelancers or find your next opportunity. One clean experience for hiring, messaging, and growing on the go.
                  </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  {highlights.map((item) => (
                    <div
                      key={item}
                      className="flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-slate-100/80 px-4 py-4 text-sm font-medium text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
                    >
                      <BadgeCheck className="h-5 w-5 flex-shrink-0 text-cyan-500" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col gap-4 sm:flex-row">
                  <a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <Button className="w-full sm:w-auto bg-slate-950 hover:bg-slate-900 dark:bg-cyan-500 dark:hover:bg-cyan-400 text-white px-7 py-6 rounded-2xl font-semibold flex items-center justify-center space-x-3 shadow-xl transition-all duration-300 transform hover:scale-[1.02]">
                      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                      </svg>
                      <div className="text-left">
                        <div className="text-xs text-white/70">GET IT ON</div>
                        <div className="text-lg font-bold">Google Play</div>
                      </div>
                    </Button>
                  </a>

                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <Button className="w-full sm:w-auto border border-slate-200 bg-white hover:bg-slate-100 text-slate-900 px-7 py-6 rounded-2xl font-semibold flex items-center justify-center space-x-3 shadow-xl transition-all duration-300 transform hover:scale-[1.02] dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10 dark:text-white">
                      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                      </svg>
                      <div className="text-left">
                        <div className="text-xs text-slate-500 dark:text-white/70">Download on the</div>
                        <div className="text-lg font-bold">App Store</div>
                      </div>
                    </Button>
                  </a>
                </div>
              </div>

              <div className="relative flex justify-center lg:justify-end">
                <div className="absolute left-0 top-10 hidden rounded-2xl border border-slate-200/80 bg-white/85 p-4 shadow-xl backdrop-blur-sm dark:border-white/10 dark:bg-[#0b1220]/85 xl:block">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Fast Setup</div>
                  <div className="mt-2 flex items-center gap-2 text-lg font-semibold text-slate-900 dark:text-white">
                    Under 2 minutes
                    <ArrowUpRight className="h-4 w-4 text-cyan-500" />
                  </div>
                </div>

                <div className="relative flex flex-col items-center gap-6 sm:flex-row sm:items-end">
                  <div className="relative z-20">
                    <div className="absolute inset-0 rounded-[3rem] bg-cyan-500/20 blur-3xl" />
                    <div className="relative w-72 max-w-full md:w-80">
                      <div className="relative rounded-[3.2rem] border-[10px] border-slate-900 bg-black p-3 shadow-[0_30px_70px_rgba(15,23,42,0.45)]">
                        <div className="absolute left-1/2 top-0 z-10 h-6 w-40 -translate-x-1/2 rounded-b-3xl bg-black"></div>

                        <div className="relative overflow-hidden rounded-[2.5rem] bg-white">
                          <img
                            src="https://customer-assets.emergentagent.com/job_4862c4cb-0240-438a-a749-b53a3e925412/artifacts/tw96waov_image.png"
                            alt="Kaamhub App"
                            className="w-full h-auto"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-[1.75rem] border border-slate-200/80 bg-white/90 p-5 shadow-2xl backdrop-blur-sm dark:border-white/10 dark:bg-white/5 sm:mb-10">
                    <div className="text-center space-y-4">
                      <div className="mx-auto flex h-36 w-36 items-center justify-center rounded-[1.4rem] bg-white p-2 dark:bg-white">
                        <QRCodeSVG
                          value={PLAY_STORE_URL}
                          size={120}
                          level="M"
                          includeMargin={false}
                        />
                      </div>
                      <div>
                        <div className="text-base font-semibold text-slate-900 dark:text-white">Scan to Download</div>
                        <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">Open the app instantly on your phone</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DownloadSection;
